import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { CREATE_CART_TRIAL } from '../../../../server';
import { Cart } from '@hiwaldo/sdk/interfaces';
import { cartSlice, customerSlice, StoreState } from '../../../redux';
import { useCreateCartEffect } from './create-cart.effect';
import { Region } from '@hiwaldo/sdk';
import { modernVersionBridge } from '../../../utils';
import { useModifyCartPrescriptionEffect } from './modify-cart-prescription.effect';
import { useStoreCatalogEffect } from '../../../../hooks';
import { useState } from 'react';
import { currentRegion } from '../../../../helpers';

type CreateCartTrialEffectReturn = {
  createCartTrial: (
    region: Region,
    trialId: number,
    planId: number,
    frequencyId: number,
    quantity: number,
    leftEyeProductVariantId: number | undefined,
    rightEyeProductVariantId: number | undefined,
    forceNew?: boolean
  ) => void;
  createCartAndTrial: (
    region: Region,
    trialId: number,
    planId: number,
    frequencyId: number,
    quantity: number,
    leftEyeProductVariantId: number | undefined,
    rightEyeProductVariantId: number | undefined,
    customerId?: number
  ) => void;
  loading: boolean;
  success: boolean;
  error: string | undefined;
};

export const useCreateCartTrialEffect = (
  callback?: () => void
): CreateCartTrialEffectReturn => {
  const dispatch = useDispatch();
  const { cart, customer, funnel, basketDataState } = useSelector(
    (state: StoreState) => ({
      cart: state.orders.cart,
      funnel: state.orders.funnel,
      customer: state.customers.customer,
      basketDataState: state.data.basket,
    })
  );

  const { createCart } = useCreateCartEffect();
  const { modifyCartPrescription } = useModifyCartPrescriptionEffect(callback);

  const region = currentRegion();
  const storeCatalog = useStoreCatalogEffect();

  const [cartTrialData, setCartTrialData] = useState<
    | {
        trialId: number;
        planId: number;
        frequencyId: number;
        quantity: number;
        leftEyeProductVariantId: number | undefined;
        rightEyeProductVariantId: number | undefined;
      }
    | undefined
  >(undefined);

  const [doCreateCartTrial, createCartTrialState] = useMutation(
    CREATE_CART_TRIAL,
    {
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        const updatedCart = data.createCartTrial as Cart;

        dispatch(cartSlice.actions.updateCart(updatedCart));
        modernVersionBridge.basket.updateCart(
          dispatch,
          basketDataState,
          updatedCart
        );

        if (updatedCart.prescription) {
          const leftEyeProductVariant = updatedCart.prescription
            ?.leftEyeProductVariant?.id
            ? storeCatalog.getOTPVariant(
                +updatedCart.prescription.leftEyeProductVariant.id,
                funnel.productLensType?.match(/vitamin/) ? 'vitamin' : 'daily'
              )
            : undefined;

          const rightEyeProductVariant = updatedCart.prescription
            ?.rightEyeProductVariant?.id
            ? storeCatalog.getOTPVariant(
                +updatedCart.prescription.rightEyeProductVariant.id,
                funnel.productLensType?.match(/vitamin/) ? 'vitamin' : 'daily'
              )
            : undefined;

          modifyCartPrescription(
            +updatedCart.prescription.id,
            leftEyeProductVariant ? +leftEyeProductVariant.id : null,
            rightEyeProductVariant ? +rightEyeProductVariant.id : null,
            customer.customer?.activePrescription
              ? customer.customer.activePrescription.id
              : undefined
          );
        } else if (callback) {
          callback();
        }
      },
      onError: (e) => {
        if (e.message.includes('Payment has already been taken for cart')) {
          if (cartTrialData) {
            createCartTrial(
              region,
              cartTrialData.trialId,
              cartTrialData.planId,
              cartTrialData.frequencyId,
              cartTrialData.quantity,
              cartTrialData.rightEyeProductVariantId,
              cartTrialData.leftEyeProductVariantId,
              true
            );
          }
        }
      },
    }
  );

  const { loading, called, error } = createCartTrialState;

  const createCartTrial = (
    region: Region,
    trialId: number,
    planId: number,
    frequencyId: number,
    quantity: number,
    leftEyeProductVariantId: number | undefined,
    rightEyeProductVariantId: number | undefined,
    forceNew = false
  ): void => {
    modernVersionBridge.basket.loading(dispatch, basketDataState, true);

    setCartTrialData({
      trialId,
      planId,
      frequencyId,
      quantity,
      leftEyeProductVariantId,
      rightEyeProductVariantId,
    });

    if (cart.cart && !forceNew) {
      const data = {
        region,
        cartId: +cart.cart.id,
        trialId,
        planId,
        frequencyId,
        quantity,
        leftEyeProductVariantId,
        rightEyeProductVariantId,
      };
      doCreateCartTrial({ variables: { data } }).then();
    } else {
      const args = {
        region: region,
        trialId,
        planId,
        frequencyId,
        quantity,
        leftEyeProductVariantId,
        rightEyeProductVariantId,
      };
      createCart(
        (args, cartId) => {
          const variables = {
            data: {
              cartId,
              region,
              trialId: args.trialId,
              planId: args.planId,
              frequencyId: args.frequencyId,
              quantity: args.quantity,
              leftEyeProductVariantId: args.leftEyeProductVariantId,
              rightEyeProductVariantId: args.rightEyeProductVariantId,
            },
          };

          doCreateCartTrial({ variables }).then();
        },
        args,
        undefined,
        forceNew
      );
    }
  };

  const createCartAndTrial = (
    region: Region,
    trialId: number,
    planId: number,
    frequencyId: number,
    quantity: number,
    leftEyeProductVariantId: number | undefined,
    rightEyeProductVariantId: number | undefined,
    customerId?: number
  ): void => {
    dispatch(customerSlice.actions.updateConverted());

    if (!cart.cart) {
      const data = {
        region,
        trialId,
        planId,
        frequencyId,
        quantity,
        leftEyeProductVariantId,
        rightEyeProductVariantId,
        customerId,
      };

      doCreateCartTrial({ variables: { data } }).then();
    }
  };

  const success = !loading && called && !error;

  return {
    createCartTrial,
    createCartAndTrial,
    loading,
    success,
    error: error?.message,
  };
};
